/**
 * A new version of a group layout the member holds, clicked in Shared with me
 * (`docs/features/group-accounts.md`, *Members choose*, owner 2026-10-18).
 *
 * Publishing no longer replaces anyone's copy, so this is where it happens —
 * when the member says so. **Replace mine** swaps their copy in place, their
 * edits gone (a Daily Brief keeps their own board). **Add as "X v3"** keeps
 * theirs, unlinked, beside the new one. **Not now** leaves the ● on the row.
 */

import type { SharedUpdateAnswer } from "../api/group-layout-sync";

export interface SharedUpdateDialogProps {
  name: string;
  /** The published version, and the day it was published as the row prints it. */
  version: number;
  publishedDay: string | null;
  /** The version the member's copy was taken at. */
  mine: number;
  onAnswer: (answer: SharedUpdateAnswer) => void;
}

export function SharedUpdateDialog({ name, version, publishedDay, mine, onAnswer }: SharedUpdateDialogProps) {
  const title = `${name} — v${version}${publishedDay ? `, published ${publishedDay}` : ""}`;
  return (
    <div style={SCRIM} onKeyDown={(e) => e.key === "Escape" && onAnswer("later")}>
      <div role="dialog" aria-modal="true" aria-label={title} style={PANEL}>
        <h4 style={TITLE}>{title}</h4>
        <p style={BODY}>{mine > 0 ? `You have v${mine}.` : "You have an earlier version."}</p>
        <p style={NOTE}>Replace mine: your copy becomes v{version}, and your edits to it are lost.</p>
        <div style={FOOT}>
          <button type="button" onClick={() => onAnswer("later")} style={BTN}>
            Not now
          </button>
          <button type="button" onClick={() => onAnswer("add")} style={BTN}>
            Add as "{name} v{version}"
          </button>
          <button type="button" autoFocus onClick={() => onAnswer("replace")} style={{ ...BTN, ...PRIMARY }}>
            Replace mine
          </button>
        </div>
      </div>
    </div>
  );
}

const SCRIM: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 1100,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(0,0,0,.35)",
};

const PANEL: React.CSSProperties = {
  width: 400,
  maxWidth: "calc(100vw - 32px)",
  padding: "14px 16px 16px",
  background: "var(--qf-surface)",
  border: "1px solid var(--qf-border)",
  borderRadius: 5,
  boxShadow: "0 14px 40px -12px rgba(0,0,0,.6)",
  color: "var(--qf-text)",
  fontSize: 12,
};

const TITLE: React.CSSProperties = { margin: "0 0 13px", fontSize: 13, fontWeight: 600 };

const BODY: React.CSSProperties = { margin: "0 0 6px" };

const NOTE: React.CSSProperties = { margin: 0, color: "var(--qf-text-muted)" };

const FOOT: React.CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  flexWrap: "wrap",
  gap: 7,
  marginTop: 16,
};

const BTN: React.CSSProperties = {
  border: "1px solid var(--qf-border)",
  background: "var(--qf-background)",
  color: "var(--qf-text)",
  borderRadius: 3,
  padding: "4px 10px",
  font: "inherit",
  fontSize: 11.5,
  cursor: "pointer",
};

const PRIMARY: React.CSSProperties = {
  background: "var(--qf-accent)",
  borderColor: "var(--qf-accent)",
  color: "var(--qf-background)",
  fontWeight: 600,
};
